import { fg } from '@atlaskit/platform-feature-flags/fg';
import { utils } from '@atlaskit/util-service-support/constants';
import type { RequestServiceOptions } from '@atlaskit/util-service-support/types';

import { InMemCache } from './in-mem-cache';
import type { AggResponse, PersistedAggRequest } from './types';

// AGG allows calling GQL endpoints where the query is persisted
// this allows us to skip sending the GQL with every call
// https://hello.atlassian.net/wiki/spaces/AGG/pages/5347991102/CLI+Persisted+Queries+-+cli-persisted-queries.md#Getting-Query-Documents
export async function fetchPersistedAggResponse<Variables, Data>({
	variables,
	operationName,
	aggUrl: url,
	headers,
	cache,
}: PersistedAggRequest<Variables>): Promise<AggResponse<Data>> {
	const options: RequestServiceOptions = {
		requestInit: {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...(headers ? { ...headers } : {}),
			},
			body: JSON.stringify({
				operationName,
				variables,
			}),
		},
		queryParams: {
			...(operationName && {
				q: operationName,
				// `operation` is added alongside `q` so UFO's Jira resource-timing
				// `sanitiseEndpoints` keys this fetch by its operation name instead
				// of collapsing it into the anonymous `gateway_api_graphql` bucket.
				...(fg('kd_fe_ufo_named_agg_resources') && { operation: operationName }),
			}),
		},
	};

	// if there's a ttl use fetch with in-mem cacheing
	if (cache && fg('kd_fe_enable_agg_cache')) {
		return fetchPersistedAggResponseWithCache<Data>({
			cache,
			aggUrl: url,
			options,
		});
	}

	return utils.requestService<AggResponse<Data>>({ url }, options);
}

async function fetchPersistedAggResponseWithCache<Data>({
	aggUrl: url,
	cache,
	options,
}: {
	cache: {
		ttl: number;
		key: string;
	};
	options: RequestServiceOptions;
	aggUrl: string;
}): Promise<AggResponse<Data>> {
	let cachedResponse = InMemCache.getData(cache.key);
	if (cachedResponse && InMemCache.isValid(cache.key)) {
		return Promise.resolve(cachedResponse);
	}

	// return a pending promise that was cached earlier
	const inflightRequest = InMemCache.getInflightRequest(cache.key);
	if (inflightRequest) {
		return inflightRequest;
	}

	// cache miss, fetch data from agg and store ref to promise
	const request: Promise<AggResponse<Data>> = new Promise(async (resolve, reject) => {
		try {
			const response = await utils.requestService<AggResponse<Data>>({ url }, options);
			InMemCache.setData({ key: cache.key, response, ttl: cache.ttl });
			resolve(response);
		} catch (error) {
			reject(error);
		} finally {
			InMemCache.removeInflightRequest(cache.key);
		}
	});

	InMemCache.setInflightRequest({ key: cache.key, request });
	return request;
}
